// Third-party packages
import {
	APP_CONF_VARS,
} from '@appConf/vars.lpdipro.conf';
import {
	EnumEnvironments,
	EnumNodeEnv,
} from '@enums/environments.enum';
import {
	EnumStatusTheme,
} from '@enums/theme.enum';
import {
	getConfig,
} from '@modules/config';
import {
	Device,
} from '@modules/device';
import {
	eventEmitter,
} from '@modules/eventEmitter';
import {
	PRIVATE_ROUTES,
} from '@routes/lpdipro/private';
import {
	EVENTS,
} from '@static_data/events.data';
import {
	useEffect,
	useState,
} from 'react';
import TagManager from 'react-gtm-module';
import {
	useTranslation,
	withTranslation,
} from 'react-i18next';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import {
	matchRoutes,
	Navigate,
	useLocation,
} from 'react-router-dom';

// Project modules
import {
	useSession,
} from '@hooks/useSession';
import {
	useSessionRefresher,
} from '@hooks/useSessionRefresher';
import {
	appSetConfig,
	appSetRoute,
} from '@stores/_slices/app';
import {
	deviceUpdate,
} from '@stores/_slices/device';
import {
	clearStatusMsgs,
} from '@stores/_slices/status_msgs';
import {
	addToastMsg,
	clearToastMsgs,
} from '@stores/_slices/toast_msgs';
import {
	GlobalUser,
	userLogout,
} from '@stores/_slices/user';
import {
	ReducerInstance,
} from '@stores/lpdipro/reducers';
import {
	selectAppState,
} from '@stores/selectors/appSelectors';
import {
	selectUserState,
} from '@stores/selectors/userSelectors';

// UI Components
import {
	Loader,
} from '@components/loader';
import {
	Toast,
	ToastProps,
} from '@components/toast';

// Relative imports
import {
	AppPrivate,
} from './_private';
import {
	AppPublic,
} from './_public';

// Style imports
import styles from './lpdipro.module.scss';
import '@stonal-tech/lib-design-system-react/lib-design-system-react.css';
import '@styles/styles.scss';

interface userFilteredProps {
	zones?: string;
	segment?: string;
	beamer?: {
		filter?: string;
	};
}

export const LpdiProApp = withTranslation()(() => {
	const dispatch = useDispatch();

	const {
		isInitialized,
		isSessionPending,
		sessionError,
	} = useSession();

	useSessionRefresher();

	const {
		t,
	} = useTranslation();
	const location = useLocation();

	const user = useSelector(selectUserState);

	const app = useSelector(selectAppState);

	const toastMsgs = useSelector((state: ReducerInstance) => state.toast_msgs?.instances);

	const initConfig = async () => {
		const fetchedConfig = await getConfig();
		const newConfig = {
			...app?.config,
			...fetchedConfig,
		};
		dispatch(appSetConfig(newConfig));
	};

	const [
		visibleToasts,
		setVisibleToasts,
	] = useState(toastMsgs?.length || 0);

	useEffect(() => {
		setVisibleToasts(toastMsgs?.length || 0);
	}, [
		toastMsgs,
	]);

	const handleToastAnimationEnd = () => {
		setVisibleToasts((prev) => prev - 1);
	};

	const toastMsgsElement = visibleToasts > 0 ? (
		<div
			className={styles.toastMsgWrapper}
			data-status-msg-wrapper
		>
			{toastMsgs?.map((toastItem: ToastProps, index: number) => (
				<Toast
					className={`${styles.toastMsgs} animation__disappearing_tooltip`}
					key={index}
					message={toastItem.message}
					onAnimationEnd={handleToastAnimationEnd}
					theme={toastItem.theme}
				/>
			))}
		</div>
	) : null;

	const isPrivateRoute = matchRoutes(PRIVATE_ROUTES, location.pathname)?.length ? true : false;

	const [
		isGTMLoadedState,
		setGTMLoadedState,
	] = useState(false);

	const getParams = window.location.search.split(/\?|&/);
	const isDebug = getParams.filter(getParam => {
		const entries = getParam.split('=').length ? getParam.split('=') : [];
		const key = entries.length ? entries[0] : '';
		const value = entries.length ? entries[1] : '';
		return key === 'debug' && value == '1';
	}).length;

	const getUserFiltered = (userState?: GlobalUser) => {
		// user keys list to not send to GTM
		const userFilter = [
			'permissions',
		];

		// user data filtered without keys
		const userFiltered = Object.fromEntries(
			Object.entries(userState || {}).filter(([
				key,
			]) => !userFilter.includes(key)),
		);

		return userFiltered;
	};

	const userFiltered = getUserFiltered(user as GlobalUser) as userFilteredProps;
	let filters = [
		'lpdipro',
	];

	// ENV
	const filterEnv = app?.config?.STONAL_ENV === EnumEnvironments.PRODUCTION ? 'env_production' : 'env_development';
	filters.push(filterEnv);

	// ZONES
	if (userFiltered.zones?.length) filters = [
		...filters,
		...userFiltered.zones,
	];

	// SEGMENT
	if (userFiltered.segment) filters.push(userFiltered.segment);
	if (app?.config?.STONAL_ENV === EnumEnvironments.PRODUCTION && userFiltered.segment) filters = filters.concat(userFiltered.segment);

	// BEAMER FILTERS
	if (userFiltered.beamer?.filter) filters = filters.concat(userFiltered.beamer.filter);

	const initialDataLayer = {
		beamer: {
			filter: filters.join('; '),
			id: APP_CONF_VARS.tools.beamer?.product_id,
		},
		env: app?.config?.NODE_ENV,
	};

	const loadGtm = () => {
		// START GOOGLE TAG MANAGER
		const tagManagerArgs = {
			dataLayer: initialDataLayer,
			gtmId: app?.config?.GOOGLE_TAG_MANAGER_ID_LPDI || '',
		};
		TagManager.initialize(tagManagerArgs);
		// END GOOGLE TAG MANAGER
	};

	useEffect(() => {
		if (!isGTMLoadedState && app?.config?.GOOGLE_TAG_MANAGER_ID_LPDI) {
			loadGtm();
			setGTMLoadedState(true);
		}
	}, [
		isGTMLoadedState,
		app?.config?.GOOGLE_TAG_MANAGER_ID_LPDI,
	]);

	const initializedDevice = new Device();

	useEffect(() => {
		dispatch(clearStatusMsgs());
		dispatch(clearToastMsgs());
		if (sessionError && isPrivateRoute && !user?.authenticated) {
			if (isInitialized) {
				dispatch(addToastMsg({
					message: t('status.error_session'),
					theme: EnumStatusTheme.ERROR,
				}));
				dispatch(userLogout());
			}
		}
	}, [
		sessionError,
		isInitialized,
		isPrivateRoute,
		user?.authenticated,
		dispatch,
	]);

	useEffect(() => {
		dispatch(clearStatusMsgs());
		dispatch(clearToastMsgs());
		initConfig();

		function handleResize() {
			initializedDevice.refresh();
			dispatch(deviceUpdate({
				display: initializedDevice.display,
				orientation: initializedDevice.orientation,
			}));
		}

		window.addEventListener('resize', handleResize);
		handleResize();

		// EVENT EMITTER SUBSCRIPTIONS
		const eventNames = Object.keys(EVENTS);
		eventNames.forEach((eventName) => {
			eventEmitter.subscribe(eventName, () => undefined);
		});

		// app is loading at the beginning of each app load
		const twigLoader = document.querySelector('.loader');
		if (twigLoader) twigLoader.remove();

		// STORING THE INITIAL ROUTE WITH QUERY PARAMS
		// This ensures we can redirect back to the exact URL after any authentication flow
		const fullPath = location.pathname + location.search;
		const hasQueryParams = location.search.length > 0;

		// Store the route if:
		// 1. It has query params (important for preserving state)
		// OR
		// 2. It's a private route and user is not authenticated (classic auth flow)
		if (hasQueryParams || (isPrivateRoute && !user?.authenticated)) {
			dispatch(appSetRoute({
				name: 'from',
				url: fullPath,
			}));
		}

		return () => {
			window.removeEventListener('resize', handleResize);
			eventNames.forEach((eventName) => {
				eventEmitter.unsubscribe(eventName);
			});
		};
	}, []);

	// EFFECT ON LOCATION CHANGE
	useEffect(() => {
		// START CUSTOM EVENT
		const data = {
			company: {
				...user?.company,
			},
			location: {
				...window.location,
				...location,
			},
			user: getUserFiltered(user as GlobalUser),
			...initialDataLayer,
		};

		// Event custom dispatched to be used with GTM
		eventEmitter.dispatch(EVENTS.LOCATION_CHANGED, data);
		// END CUSTOM EVENT
	}, [
		location,
	]);

	// EFFECT ON USER.AUTHENTICATED STATE CHANGE
	useEffect(() => {
		// data object to send to GTM
		const data = {
			...initialDataLayer,
			company: user?.authenticated ? user?.company : {
				id: 0,
				name: '',
			},
			user: user?.authenticated ? getUserFiltered(user as GlobalUser) : {},
		};
		// send data to GTM
		eventEmitter.dispatch(user?.authenticated ? EVENTS.USER_LOGGED_IN : EVENTS.USER_LOGGED_OUT, data);
	}, [
		user?.authenticated,
	]);

	let appVersion = (
		<Loader
			message={t('general.loading')}
		/>
	);
	if (!isSessionPending && user?.authenticated) {
		appVersion = <AppPrivate />;
	} else if (!isSessionPending && isInitialized) {
		appVersion = <AppPublic />;
	} else if (!isSessionPending && !isInitialized && !isPrivateRoute) {
		appVersion = <AppPublic />;
	} else if (!isSessionPending && !user?.authenticated && isPrivateRoute) {
		appVersion = <Navigate
			to="/login"
			replace
		/>;
	}

	return (
		<div className={`${styles.App} ${isDebug && process.env.NODE_ENV !== EnumNodeEnv.PRODUCTION ? 'debug' : ''}`} >
			{toastMsgsElement}
			{appVersion}
		</div>
	);
});
